import React, { useState, useEffect, useRef, useMemo } from 'react'
import { CSSTransition } from 'react-transition-group'
import Modal from 'react-modal'
import { useTranslation } from 'react-i18next'
import styles from './tournament-chart-mobile-filter.module.scss'
import BtnFilterMobile from 'components/generic/BtnFilterMobile'
import BtnToggle from 'components/generic/BtnToggle'
import BtnWithChevron from 'components/generic/BtnWithChevron'
import BtnWithCheck from 'components/generic/BtnWithCheck'
import CountriesFilterMobile from './CountriesFilterMobile'
import MobileFilterProps from './MobileFilterSelectorTypes'
import { GenderFilter, RatingFilter, SortingDirection, SortingField } from 'types/global'
import BtnPrimary from '../BtnPrimary'
import CombinedRatingFilterMobile from './CombinedRatingFilterMobile'
import cx from 'classnames'
import MobileFilterSingleSelection from './MobileFilterSingleSelection'
import FilterMenuHeader from './FilterMenuHeader'
import { GameType } from 'components/chart/ResultsDoughnutChart/ResultsDoughnutChartTypes'
import { DateRange } from '../DatePicker/types'
import DateRangePickerWTN from '../DatePicker/DateRangePickerWTN'

/**  Creates a mobile modal with filter and sorting selections depending on the props being passed
when all selections are made either cancels all selections or dispatches actions to update the redux store(global state)*/

const MobileFilter: React.FC<MobileFilterProps> = ({
  sortingSelectionData,
  genderFilterData,
  ageGroupFilterData,
  yearFilterData,
  gameTypeFilterData,
  timePeriodFilterData,
  timeRangeFilterData,
  countriesFilterData,
  ratingRangesFilterData,
  resetGlobalFilters,
  classNames,
  appliedFiltersAndSortCount,
}) => {
  const { t } = useTranslation('strMobileFilter')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [datesModalOpen, setDatesModalOpen] = useState(false)
  const toggleModal = () => setIsModalOpen(!isModalOpen)
  const toggleDatesModal = () => setDatesModalOpen(!datesModalOpen)
  const [yearFilter, setYearFilter] = useState<number | undefined>(yearFilterData?.selectedYearItem)
  const [genderFilter, setGenderFilter] = useState<GenderFilter>(
    genderFilterData?.selectedGenderItem ?? GenderFilter.ANY
  )
  const [ageGroupFilter, setAgeGroupFilter] = useState<string>(
    ageGroupFilterData?.selectedAgeGroup ?? ''
  )
  const [gameTypeFilter, setGameTypeFilter] = useState<GameType | undefined>(
    gameTypeFilterData?.selectedGameTypeItem
  )
  const [timePeriodFilter, setTimePeriodFilter] = useState<number | null | undefined>(
    timePeriodFilterData?.selectedTimePeriodItem
  )
  const [timeRangeStart, setTimeRangeStart] = useState<any>(timeRangeFilterData?.startDate)
  const [timeRangeEnd, setTimeRangeEnd] = useState<any>(timeRangeFilterData?.endDate)
  const dateRangeParams = useMemo(() => {
    if (!timeRangeFilterData) return null

    return {
      minDate: new Date(timeRangeFilterData.minDate),
      maxDate: new Date(timeRangeFilterData.maxDate),
      selectedRange: {
        start: new Date(timeRangeFilterData.startDate),
        end: new Date(timeRangeFilterData.endDate),
      },
    }
  }, [timeRangeFilterData])
  const [countriesFilter, setCountriesFilter] = useState<string[] | undefined>(
    countriesFilterData?.selectedCountries
  )
  const [ratingFilter, setRatingFilter] = useState<RatingFilter | null>(
    ratingRangesFilterData?.selectedRating ?? null
  )
  const [sorting, setSorting] = useState<SortingField | undefined>(
    sortingSelectionData?.selectedSorting
  )
  const section = useRef<HTMLElement>(null)

  const cancelLocalStateChoices = () => {
    sortingSelectionData && setSorting(sortingSelectionData?.selectedSorting)
    genderFilterData && setGenderFilter(genderFilterData?.selectedGenderItem)
    ageGroupFilterData && setAgeGroupFilter(ageGroupFilterData?.selectedAgeGroup ?? '')
    yearFilterData && setYearFilter(yearFilterData?.selectedYearItem)
    gameTypeFilterData && setGameTypeFilter(gameTypeFilterData?.selectedGameTypeItem)
    countriesFilterData && setCountriesFilter(countriesFilterData?.selectedCountries)
    timePeriodFilterData && setTimePeriodFilter(timePeriodFilterData?.selectedTimePeriodItem)
    timeRangeFilterData && setTimeRangeStart(timeRangeFilterData?.startDate)
    setTimeRangeEnd(timeRangeFilterData?.endDate)
    ratingRangesFilterData && setRatingFilter(ratingRangesFilterData.selectedRating)
  }

  const resetLocalAndGlobalState = () => {
    resetGlobalFilters?.()
    sortingSelectionData?.setSorting(sortingSelectionData.defaultSorting)
    setGenderFilter(GenderFilter.ANY)
    setSorting(sortingSelectionData?.defaultSorting?.field)
  }

  const updateGlobalStateOnExit = () => {
    sortingSelectionData?.setSorting(
      sorting ? { direction: SortingDirection.ASC, field: sorting as SortingField } : null
    )

    genderFilterData?.genderDispatch(
      genderFilter ? genderFilter : genderFilterData.selectedGenderItem
    )

    ageGroupFilterData?.ageGroupDispatch(ageGroupFilter || undefined)

    yearFilterData?.yearDispatch(yearFilter ? yearFilter : yearFilterData.selectedYearItem)

    gameTypeFilterData?.gameTypeDispatch(
      gameTypeFilter ? gameTypeFilter : gameTypeFilterData.selectedGameTypeItem
    )

    countriesFilterData?.countriesDispatch(countriesFilter)

    timePeriodFilterData?.updateTimePeriodFilterValue?.(timePeriodFilter ?? null)

    timeRangeFilterData?.timeRangeDispatch({
      start: timeRangeStart,
      end: timeRangeEnd,
    })
    console.log(ratingFilter)
    ratingRangesFilterData?.setRating(ratingFilter)
  }

  const handleCalendarCallback = (dates: DateRange) => {
    const { start, end } = dates
    setTimeRangeStart(start?.toISOString())
    setTimeRangeEnd(end?.toISOString())
  }

  useEffect(() => {
    Modal.setAppElement('body')
  }, [])

  return (
    <>
      <BtnFilterMobile
        onClick={toggleModal}
        className={cx(styles.filterBar, classNames?.filterBar, {
          '--is-active': !!appliedFiltersAndSortCount,
        })}
      >
        {!!appliedFiltersAndSortCount
          ? t('filtersWithCount', { count: appliedFiltersAndSortCount })
          : t('filters')}
      </BtnFilterMobile>

      <CSSTransition
        in={isModalOpen}
        timeout={300}
        classNames='dialog'
        onEnter={cancelLocalStateChoices}
        onExited={cancelLocalStateChoices}
      >
        <Modal closeTimeoutMS={500} isOpen={isModalOpen} className={styles.modalWrapper}>
          <FilterMenuHeader
            title={t('filters')}
            btnLeft={{
              content: t('cancel'),
              onClick: () => setIsModalOpen(false),
            }}
            btnRight={
              countriesFilterData?.searchId
                ? {
                    content: t('clearAll'),
                    onClick: () => {
                      setIsModalOpen(false)
                      resetLocalAndGlobalState()
                    },
                  }
                : undefined
            }
          />

          <div className={styles.body}>
            {sortingSelectionData && (
              <MobileFilterSingleSelection<SortingField | undefined>
                id='sorting'
                filterLabel={t('sortBy')}
                options={sortingSelectionData.sortingSelectionItems}
                selectedValue={sorting}
                setValue={setSorting}
              />
            )}

            {genderFilterData && (
              <MobileFilterSingleSelection<GenderFilter>
                id='gender'
                filterLabel={t('gender')}
                options={genderFilterData.genderItems}
                setValue={setGenderFilter}
                selectedValue={genderFilter}
              />
            )}

            {ageGroupFilterData && (
              <MobileFilterSingleSelection
                id='ageGroup'
                filterLabel={t('ageGroup')}
                options={ageGroupFilterData.ageGroupItems}
                setValue={setAgeGroupFilter}
                selectedValue={ageGroupFilter}
              />
            )}

            {countriesFilterData && (
              <CountriesFilterMobile
                filterCountries={countriesFilter}
                setCountries={setCountriesFilter}
              />
            )}

            {ratingRangesFilterData && (
              <>
                <CombinedRatingFilterMobile
                  id={'wtn-rating'}
                  title={t('rating')}
                  rating={ratingRangesFilterData.selectedRating}
                  setRating={setRatingFilter}
                  minRange={ratingRangesFilterData.minRange}
                  maxRange={ratingRangesFilterData.maxRange}
                  stepSize={ratingRangesFilterData.stepSize}
                  reset={ratingRangesFilterData.resetFilters}
                />
              </>
            )}

            {gameTypeFilterData && (
              <section>
                <h2>{t('gameType')}</h2>
                <div className={styles.btnGroupWrapper}>
                  {gameTypeFilterData.gameTypeItems.map(item => (
                    <BtnToggle
                      isActive={gameTypeFilter === item.key}
                      onClick={() => setGameTypeFilter(item.key)}
                      key={item.key}
                    >
                      {item.label}
                    </BtnToggle>
                  ))}
                </div>
              </section>
            )}

            {timePeriodFilterData && (
              <section>
                <h2>{t('timePeriod')}</h2>
                <div className={styles.btnGroupWrapper}>
                  {timePeriodFilterData.timePeriodItems.map((item, index) => (
                    <BtnToggle
                      isActive={timePeriodFilter === item.key}
                      onClick={() => setTimePeriodFilter(item.key)}
                      key={index}
                    >
                      {item.label}
                    </BtnToggle>
                  ))}
                </div>
              </section>
            )}

            {yearFilterData && (
              <>
                <section>
                  <h2 className={styles.titleBtnGroupWrapper}>
                    {t('year')}{' '}
                    <BtnWithChevron onClick={toggleDatesModal}>{yearFilter}</BtnWithChevron>
                  </h2>
                </section>

                <Modal
                  closeTimeoutMS={500}
                  isOpen={datesModalOpen}
                  className={`${styles.modalWrapper}`}
                >
                  <header className={styles.header}>
                    <div className={styles.container}>
                      <button onClick={toggleDatesModal}>{t('goBack')}</button>
                      <h1>{t('year')}</h1>
                    </div>
                  </header>

                  <div className={`${styles.filterSelectionView}`}>
                    {yearFilterData.yearItems?.map(item => (
                      <BtnWithCheck
                        isActive={yearFilter === item.key ? true : false}
                        onClick={() => setYearFilter(item.key)}
                        key={item.key}
                      >
                        {item.label}
                      </BtnWithCheck>
                    ))}
                  </div>
                </Modal>
              </>
            )}

            {timeRangeFilterData && dateRangeParams && (
              <section ref={section} className={styles.datePickerSection}>
                <h2>Date range</h2>
                <DateRangePickerWTN
                  {...dateRangeParams}
                  wrapperClassName={styles.rangePickerWrapper}
                  inputClassName={styles.rangePicker}
                  setSelectedRange={handleCalendarCallback}
                  isMobileFilter
                  hideButtons
                />
              </section>
            )}
          </div>

          <footer className={styles.footer}>
            <BtnPrimary
              size={'medium'}
              onClick={() => {
                updateGlobalStateOnExit()
                toggleModal()
              }}
            >
              {t('updateFilters')}
            </BtnPrimary>
          </footer>
        </Modal>
      </CSSTransition>
    </>
  )
}

export default MobileFilter
